import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Col,
  Space,
  Switch,
  Table,
  Typography,
  message,
  Tooltip,
  Modal,
  Select,
  DatePicker,
  Alert,
} from 'antd';
import ModernFormModal from '../components/ModernFormModal';
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  UserSwitchOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import { masterDataService, Vendor } from '../services/masterDataService';
import VendorDetailsModal from '../components/VendorDetailsModal';
import {
  createVendorSettlement,
  previewVendorSettlement,
  TdsPreview,
} from '../services/vendorSettlementService';
import { formatNu } from '../utils/formatAmount';
import './Vendors.css';

const { Title, Text } = Typography;

/** Local digits after +975 — same pattern as Add Company phone fields. */
function vendorPhoneToLocalInput(stored?: string | null): string {
  if (!stored) return '';
  const s = String(stored).trim();
  if (s.startsWith('+975')) return s.slice(4).replace(/\D/g, '');
  if (s.startsWith('975')) return s.slice(3).replace(/\D/g, '');
  return s.replace(/\D/g, '');
}

function vendorPhoneFromLocalInput(local: string | undefined): string | undefined {
  const digits = String(local || '').replace(/\D/g, '');
  if (!digits) return undefined;
  return `+975${digits}`;
}

type VendorFormValues = {
  name: string;
  code: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
  payment_terms?: number;
  tds_applicable?: boolean;
  tds_rate?: number | null;
  is_active?: boolean;
};

const Vendors: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [searchText, setSearchText] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [form] = Form.useForm<VendorFormValues>();
  const [viewingVendor, setViewingVendor] = useState<Vendor | null>(null);
  const [settleVendor, setSettleVendor] = useState<Vendor | null>(null);
  const [settleSaving, setSettleSaving] = useState(false);
  const [settlePreview, setSettlePreview] = useState<TdsPreview | null>(null);
  const [settleForm] = Form.useForm();

  const canRead = RBACService.hasPermission(PERMISSIONS.VENDOR_READ);
  const canCreate = RBACService.hasPermission(PERMISSIONS.VENDOR_CREATE);
  const canUpdate = RBACService.hasPermission(PERMISSIONS.VENDOR_UPDATE);
  const canDelete = RBACService.hasPermission(PERMISSIONS.VENDOR_DELETE);
  const canManageVendorProducts =
    RBACService.canWriteVendorCatalog() || RBACService.canDeleteVendorCatalogLine();
  const canViewVendorList = canRead || canManageVendorProducts;

  const fetchVendors = useCallback(async () => {
    if (!canViewVendorList) return;
    setLoading(true);
    try {
      const data = await masterDataService.getVendors({
        active_only: activeOnly,
        search: searchText || undefined,
        limit: 500,
      });
      setVendors(data || []);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  }, [activeOnly, searchText, canViewVendorList]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ payment_terms: 30, is_active: true, tds_applicable: false, tds_rate: undefined });
    setModalOpen(true);
  };

  const openEdit = (v: Vendor) => {
    setEditing(v);
    form.resetFields();
    form.setFieldsValue({
      name: v.name,
      code: v.code,
      contact_person: v.contact_person,
      email: v.email || v.contact_email,
      phone: vendorPhoneToLocalInput(v.phone || v.contact_phone),
      address: v.address,
      payment_terms: v.payment_terms ?? 30,
      tds_applicable: Boolean(v.tds_applicable),
      tds_rate: v.tds_rate ?? undefined,
      is_active: v.is_active,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: VendorFormValues) => {
    try {
      const payload: VendorFormValues = {
        ...values,
        phone: vendorPhoneFromLocalInput(values.phone),
      };

      if (!editing) {
        if (!canCreate) return message.error('You do not have permission to create vendors');
        await masterDataService.createVendor({
          ...payload,
          is_active: payload.is_active ?? true,
        } as any);
        message.success('Vendor created');
      } else {
        if (!canUpdate) return message.error('You do not have permission to update vendors');
        await masterDataService.updateVendor(editing.id, {
          ...payload,
          name: editing.name,
          code: editing.code,
        } as any);
        message.success('Vendor updated');
      }
      setModalOpen(false);
      setEditing(null);
      fetchVendors();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || err?.message || 'Failed to save vendor');
    }
  };

  const handleDeleteVendor = async (id: number) => {
    if (!canDelete) return message.error('You do not have permission to delete vendors');
    try {
      await masterDataService.deleteVendor(id);
      message.success('Vendor deleted');
      fetchVendors();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const handleActivate = async (v: Vendor) => {
    if (!canUpdate) return message.error('You do not have permission to activate vendors');
    try {
      await masterDataService.updateVendor(v.id, { is_active: true } as any);
      message.success('Vendor activated');
      fetchVendors();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to activate vendor');
    }
  };

  const filteredCount = vendors.length;
  const activeCount = useMemo(() => vendors.filter(v => v.is_active).length, [vendors]);

  const columns: ColumnsType<Vendor> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 140,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: any, v: Vendor) => {
        const email = v.email || v.contact_email;
        const phone = v.phone || v.contact_phone;
        return (
          <div style={{ lineHeight: 1.35 }}>
            <div>{v.contact_person || '-'}</div>
            <div style={{ color: '#111827', fontSize: 12 }}>{phone || 'No phone'}</div>
            {email ? (
              <div style={{ color: '#6b7280', fontSize: 12 }}>{email}</div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'TDS',
      key: 'tds',
      width: 110,
      render: (_: any, v: Vendor) =>
        v.tds_applicable ? (
          <Badge status="processing" text={`${Number(v.tds_rate ?? 0)}%`} />
        ) : (
          <span style={{ color: '#9ca3af' }}>Off</span>
        ),
    },
    {
      title: 'Payment Terms',
      dataIndex: 'payment_terms',
      key: 'payment_terms',
      width: 140,
      render: (v?: number) => `${v ?? 30} days`,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (is_active: boolean) => (
        <Badge status={is_active ? 'success' : 'error'} text={is_active ? 'Active' : 'Inactive'} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_: any, v: Vendor) => (
        <Space size={8}>
          {(canRead || canManageVendorProducts) && (
            <Tooltip title="View vendor details">
              <Button
                className="icon-btn icon-btn--default"
                shape="round"
                aria-label="View vendor details"
                icon={<EyeOutlined />}
                onClick={() => setViewingVendor(v)}
              />
            </Tooltip>
          )}
          {canUpdate && (
            <Tooltip title="Record supplier settlement (TDS split)">
              <Button
                className="icon-btn icon-btn--default"
                shape="round"
                aria-label="Record settlement"
                icon={<DollarOutlined />}
                onClick={() => {
                  setSettleVendor(v);
                  setSettlePreview(null);
                  settleForm.resetFields();
                  settleForm.setFieldsValue({
                    settlement_date: moment(),
                    gross_amount: undefined,
                    payment_method: 'BANK',
                    apply_tds: Boolean(v.tds_applicable),
                    tds_rate: v.tds_rate ?? 2,
                  });
                }}
              />
            </Tooltip>
          )}
          {canUpdate && (
            <Button
              className="icon-btn icon-btn--primary"
              shape="round"
              aria-label="Edit"
              icon={<EditOutlined />}
              onClick={() => openEdit(v)}
            />
          )}
          {!v.is_active && canUpdate && (
            <Popconfirm
              title="Activate this vendor?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleActivate(v)}
            >
              <Button
                className="icon-btn icon-btn--success"
                shape="round"
                aria-label="Activate"
                icon={<CheckOutlined />}
              />
            </Popconfirm>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this vendor permanently? This cannot be undone. Deletion is blocked if purchase orders exist for this vendor."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={() => handleDeleteVendor(v.id)}
            >
              <Button
                className="icon-btn icon-btn--danger"
                shape="round"
                aria-label="Delete vendor"
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  if (!canViewVendorList) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>Vendors</Title>
        <div>You do not have permission to view vendors or vendor product links.</div>
      </div>
    );
  }

  return (
    <div className="vendors-page" style={{ minHeight: '100dvh' }}>
      <div className="vendors-header">
        <div className="vendors-header__title-wrap">
          <div className="vendors-header__icon"><UserSwitchOutlined /></div>
          <div>
            <Title level={2} className="vendors-header__title">Vendors</Title>
            <div className="vendors-header__subtitle">
              {activeCount} active / {filteredCount} shown
            </div>
          </div>
        </div>
      </div>

      <Card className="page-table-card">
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <Input.Search
              className="app-search"
              placeholder="Search by name or code"
              allowClear
              size="large"
              enterButton
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={(v) => setSearchText(v || '')}
              style={{ width: 300 }}
            />
            <Button
              onClick={() => setActiveOnly(a => !a)}
            >
              {activeOnly ? 'Show Inactive Too' : 'Active Only'}
            </Button>
          </Space>
          <Space>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Add Vendor
              </Button>
            )}
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={vendors}
          rowKey="id"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>

      <ModernFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
        }}
        title={editing ? `Edit Vendor - ${editing.code}` : 'Add New Vendor'}
        subtitle={
          editing
            ? 'Update vendor details below.'
            : 'Fill in the details below to add a new vendor to the system.'
        }
        headerVariant="light"
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        okText={editing ? 'Update Vendor' : 'Create Vendor'}
        onOk={() => form.submit()}
        isSubmitting={loading}
        okDisabled={editing ? !canUpdate : !canCreate}
        destroyOnClose={false}
      >
        <div className="vendor-form-modal-body" style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off" style={{ width: '100%' }}>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="name"
                  label="Vendor Name"
                  rules={editing ? [] : [{ required: true, message: 'Vendor name is required' }]}
                  tooltip={editing ? 'Name cannot be changed after the vendor is created.' : undefined}
                >
                  <Input
                    placeholder="Enter vendor name"
                    style={{ width: '100%' }}
                    disabled={!!editing}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="code"
                  label="Vendor Code"
                  rules={editing ? [] : [{ required: true, message: 'Vendor code is required' }]}
                  tooltip={editing ? 'Code cannot be changed after the vendor is created.' : undefined}
                >
                  <Input
                    placeholder="e.g., VEND-001"
                    style={{ width: '100%' }}
                    disabled={!!editing}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="contact_person" label="Contact Person Name">
                  <Input placeholder="Optional" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="payment_terms" label="Payment Terms (days)">
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="email" label="Email">
                  <Input placeholder="Optional" type="email" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phone" label="Phone">
                  <Input
                    autoComplete="off"
                    addonBefore={<span className="ac-phone-prefix">+975</span>}
                    placeholder="XXXXXXXX"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="address" label="Address">
              <Input.TextArea rows={3} placeholder="Optional" style={{ width: '100%' }} />
            </Form.Item>

            <div style={{ marginBottom: 8, fontWeight: 600 }}>TDS (Tax Deducted at Source)</div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Leave off for most vendors. When on, recording a supplier settlement withholds TDS for DRC —
              GRPO and stock are never changed by this.
            </Text>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="tds_applicable" label="Apply TDS on settlements" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item shouldUpdate={(prev, cur) => prev.tds_applicable !== cur.tds_applicable} noStyle>
                  {({ getFieldValue }) =>
                    getFieldValue('tds_applicable') ? (
                      <Form.Item
                        name="tds_rate"
                        label="TDS rate (%)"
                        rules={[{ required: true, message: 'Enter TDS rate' }]}
                      >
                        <InputNumber min={0.01} max={100} step={0.01} style={{ width: '100%' }} />
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </ModernFormModal>

      <Modal
        title={settleVendor ? `Settle supplier — ${settleVendor.name}` : 'Supplier settlement'}
        open={!!settleVendor}
        onCancel={() => {
          setSettleVendor(null);
          setSettlePreview(null);
        }}
        onOk={() => settleForm.submit()}
        okText="Record settlement"
        confirmLoading={settleSaving}
        destroyOnClose
        width={520}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Does not change stock or GRPO"
          description="This only records what you pay the supplier vs what you withhold for DRC (TDS)."
        />
        <Form
          form={settleForm}
          layout="vertical"
          onFinish={async (values) => {
            if (!settleVendor) return;
            setSettleSaving(true);
            try {
              const payload = {
                vendor_id: settleVendor.id,
                settlement_date: values.settlement_date.format('YYYY-MM-DD'),
                gross_amount: Number(values.gross_amount),
                payment_method: values.payment_method,
                payment_reference: values.payment_reference || null,
                withholding_certificate_no: values.withholding_certificate_no || null,
                notes: values.notes || null,
                apply_tds: Boolean(values.apply_tds),
                tds_rate: values.apply_tds ? Number(values.tds_rate) : null,
              };
              const row = await createVendorSettlement(payload);
              message.success(
                `Settlement ${row.settlement_number}: pay supplier ${formatNu(row.pay_to_supplier)}, withhold ${formatNu(row.withhold_for_drc)} for DRC`,
              );
              setSettleVendor(null);
              setSettlePreview(null);
            } catch (err: any) {
              message.error(err?.response?.data?.detail || 'Failed to record settlement');
            } finally {
              setSettleSaving(false);
            }
          }}
        >
          <Form.Item
            name="settlement_date"
            label="Settlement date"
            rules={[{ required: true, message: 'Date required' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="gross_amount"
            label="Gross amount (invoice / bill)"
            rules={[{ required: true, message: 'Enter gross amount' }]}
          >
            <InputNumber
              min={0.01}
              style={{ width: '100%' }}
              formatter={(value) => `Nu. ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value!.replace(/Nu.\s?|(,*)/g, '') as any}
              onBlur={async () => {
                if (!settleVendor) return;
                const values = settleForm.getFieldsValue();
                if (!values.gross_amount || !values.settlement_date) return;
                try {
                  const preview = await previewVendorSettlement({
                    vendor_id: settleVendor.id,
                    settlement_date: values.settlement_date.format('YYYY-MM-DD'),
                    gross_amount: Number(values.gross_amount),
                    apply_tds: Boolean(values.apply_tds),
                    tds_rate: values.apply_tds ? Number(values.tds_rate) : null,
                  });
                  setSettlePreview(preview);
                } catch {
                  setSettlePreview(null);
                }
              }}
            />
          </Form.Item>
          <Form.Item name="apply_tds" label="Withhold TDS" valuePropName="checked">
            <Switch
              onChange={async () => {
                const values = settleForm.getFieldsValue();
                if (!settleVendor || !values.gross_amount || !values.settlement_date) return;
                try {
                  const preview = await previewVendorSettlement({
                    vendor_id: settleVendor.id,
                    settlement_date: values.settlement_date.format('YYYY-MM-DD'),
                    gross_amount: Number(values.gross_amount),
                    apply_tds: Boolean(settleForm.getFieldValue('apply_tds')),
                    tds_rate: settleForm.getFieldValue('apply_tds')
                      ? Number(settleForm.getFieldValue('tds_rate'))
                      : null,
                  });
                  setSettlePreview(preview);
                } catch {
                  setSettlePreview(null);
                }
              }}
            />
          </Form.Item>
          <Form.Item shouldUpdate={(p, c) => p.apply_tds !== c.apply_tds} noStyle>
            {({ getFieldValue }) =>
              getFieldValue('apply_tds') ? (
                <Form.Item name="tds_rate" label="TDS rate (%)" rules={[{ required: true }]}>
                  <InputNumber min={0.01} max={100} step={0.01} style={{ width: '100%' }} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          {settlePreview ? (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message="Payment split"
              description={
                <div>
                  <div>Pay to supplier: <strong>{formatNu(settlePreview.pay_to_supplier)}</strong></div>
                  <div>Withhold for DRC (TDS): <strong>{formatNu(settlePreview.withhold_for_drc)}</strong></div>
                </div>
              }
            />
          ) : null}
          <Form.Item name="payment_method" label="Pay supplier via" initialValue="BANK">
            <Select
              options={[
                { value: 'BANK', label: 'Bank / mBoB' },
                { value: 'CASH', label: 'Cash' },
              ]}
            />
          </Form.Item>
          <Form.Item name="payment_reference" label="Bank / payment reference">
            <Input placeholder="Optional" />
          </Form.Item>
          <Form.Item name="withholding_certificate_no" label="Withholding certificate no.">
            <Input placeholder="Optional — for your TDS records" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {viewingVendor && (
        <VendorDetailsModal
          open
          vendor={viewingVendor}
          onClose={() => setViewingVendor(null)}
        />
      )}
    </div>
  );
};

export default Vendors;

